import { DeepPartial } from "@hypertune/sdk";
import { diff } from "deep-object-diff";
import {
  getSchemaWithDescriptions,
  reconcileSchemaAndImplementation,
  splitSchemaCode,
} from "@hypertune/shared-internal";
import {
  Schema,
  SplitMap,
  asError,
  ValueType,
} from "@hypertune/sdk/src/shared";
import { getLogicErrorMessage } from "@hypertune/shared-internal/src/expression/getExpressionRecursiveErrorMessages";
import {
  ExpressionMap,
  getExpressionFromMap,
  getExpressionMap,
} from "@hypertune/shared-internal/src/expressionMap";
import { getSplitsErrorMessage } from "@hypertune/shared-internal/src/expression/getSplitErrorMessage";
import { DraftCommit } from "../projectSlice";

export type CommitData = DraftCommit & {
  id: number;
};

type RebaseCommitData = {
  schema: Schema;
  splits: SplitMap;
  expressionMap: ExpressionMap;
};

export default function squashAndRebase({
  commonAncestorCommit,
  intoBranchCommit,
  fromBranchCommit,
}: {
  commonAncestorCommit: CommitData;
  intoBranchCommit: CommitData;
  fromBranchCommit: CommitData;
}): DraftCommit & {
  schemaError: string | null;
  logicError: string | null;
  splitsError: string | null;
} {
  if (intoBranchCommit.id === commonAncestorCommit.id) {
    // When the from branch is up to data with into branch we can just use
    // the latest from branch commit.
    const { id: _, ...data } = fromBranchCommit;
    return {
      ...data,
      schemaError: null,
      logicError: null,
      splitsError: null,
    };
  }
  const commonAncestorData = getRebaseCommitData(commonAncestorCommit);
  const intoBranchData = getRebaseCommitData(intoBranchCommit);
  const fromBranchData = getRebaseCommitData(fromBranchCommit);

  const diffToApply = getCommitDiff(commonAncestorData, fromBranchData);

  const newCommitData = applyDiffToCommit(
    commonAncestorData,
    intoBranchData,
    fromBranchData,
    diffToApply
  );

  const newRawSchema = sanitizeSchema(newCommitData.schema);

  const newRawExpression = getExpressionFromMap(
    newCommitData.expressionMap,
    fromBranchData.expressionMap,
    {
      type: "ExpressionMapPointer",
      id: fromBranchCommit.expression!.id,
    }
  );

  console.debug("squashAndRebase", {
    commonAncestorData,
    intoBranchData,
    fromBranchData,
    diffToApply,
    newCommitData,
    newRawExpression,
  });

  const {
    newSchema,
    newSchemaCode,
    newEventTypeMap,
    newSplits,
    newExpression,
  } = reconcileSchemaAndImplementation(
    newRawSchema,
    newCommitData.splits,
    { ...intoBranchCommit.eventTypes, ...fromBranchCommit.eventTypes },
    newRawExpression!
  );
  let schemaError: string | null = null;
  let logicError: string | null = null;
  let splitsError: string | null = null;

  try {
    getSchemaWithDescriptions(newSchemaCode);
  } catch (error) {
    schemaError = asError(schemaError).message;
  }
  if (schemaError === null) {
    logicError = getLogicErrorMessage(newSchema, newExpression, newSplits);
    splitsError = getSplitsErrorMessage(newSchema, newSplits);
  }
  const { readOnlySchemaCode, editableSchemaCode } = splitSchemaCode(
    newSchema,
    newSchemaCode
  );

  return {
    schema: newSchema,
    schemaCode: newSchemaCode,
    readOnlySchemaCode,
    editableSchemaCode,
    eventTypes: newEventTypeMap,
    splits: newSplits,
    expression: newExpression,
    schemaError,
    logicError,
    splitsError,
  };
}

function getCommitDiff(
  baseCommit: RebaseCommitData,
  newCommit: RebaseCommitData
): DeepPartial<RebaseCommitData> {
  return diff(baseCommit, newCommit);
}

function applyDiffToCommit(
  commonAncestorCommit: RebaseCommitData,
  baseCommit: RebaseCommitData,
  newCommit: RebaseCommitData,
  diffData: DeepPartial<RebaseCommitData>
): RebaseCommitData {
  return applyDiffToData({
    commonAncestorCommit,
    baseData: baseCommit,
    newData: newCommit,
    diffData,
  });
}

function applyDiffToData({
  commonAncestorCommit,
  baseData,
  newData,
  diffData,
}: {
  commonAncestorCommit: any;
  baseData: any;
  newData: any;
  diffData: any;
}): any {
  if (diffData === undefined) {
    // Undefined means deleted.
    return undefined;
  }
  if (baseData === undefined) {
    // Missing base data means data is only present in the new value.
    return newData;
  }

  if (
    baseData &&
    newData &&
    typeof baseData === "object" &&
    typeof newData === "object" &&
    typeof diffData === "object"
  ) {
    if (
      (isExpressionOrValueType(baseData) || isExpressionOrValueType(newData)) &&
      baseData.type !== newData.type
    ) {
      // When we get expression or value types that don't match fall back to
      // newData
      return newData;
    }

    return Object.fromEntries([
      // Start with base data
      ...Object.entries(baseData),
      // Then override based on the diff
      ...Object.entries(diffData)
        .map(([fieldName, fieldDiffData]) => {
          return [
            fieldName,
            applyDiffToData({
              commonAncestorCommit: commonAncestorCommit?.[fieldName],
              baseData: baseData[fieldName],
              newData: newData[fieldName],
              diffData: fieldDiffData,
            }),
          ];
        })
        // Filter to remove deleted fields
        .filter(([, value]) => value !== undefined),
    ]);
  }

  return newData;
}

function isExpressionOrValueType(data: object): boolean {
  return (
    data &&
    "type" in data &&
    !!data.type &&
    typeof data.type === "string" &&
    (data.type.endsWith("Expression") || data.type.endsWith("ValueType"))
  );
}

function getRebaseCommitData(commit: CommitData): RebaseCommitData {
  return {
    schema: commit.schema,
    splits: commit.splits,
    expressionMap: getExpressionMap(commit.expression!),
  };
}

function sanitizeSchema(schema: Schema): Schema {
  return {
    ...schema,
    objects: Object.fromEntries(
      Object.entries(schema.objects).map(([objectTypeName, objectSchema]) => [
        objectTypeName,
        {
          ...objectSchema,
          fields: Object.fromEntries(
            Object.entries(objectSchema.fields).map(
              ([fieldName, fieldSchema]) => [
                fieldName,
                {
                  ...fieldSchema,
                  valueType: sanitizeValueType(fieldSchema.valueType),
                },
              ]
            )
          ),
        },
      ])
    ),
  };
}

/**
 * Recursively sanitizes the value type by ensuring the parameterValueTypes of
 * any FunctionValueType are arrays instead of index-keyed objects, by calling
 * Object.values() on it.
 */
function sanitizeValueType(valueType: ValueType): ValueType {
  if (valueType.type === "FunctionValueType") {
    return {
      ...valueType,
      parameterValueTypes: Object.values(valueType.parameterValueTypes).map(
        sanitizeValueType
      ),
      returnValueType: sanitizeValueType(valueType.returnValueType),
    };
  }

  if (valueType.type === "ListValueType") {
    return {
      ...valueType,
      itemValueType: sanitizeValueType(valueType.itemValueType),
    };
  }

  // For all other value types, return as-is since they don't contain nested
  // ValueTypes
  return valueType;
}
